/**
 * The visitor's end of the referral link.
 *
 * Someone clicks re-XXXX and lands on the app. They will not necessarily sign up
 * in that first minute — they read the page, poke around, close the tab, come
 * back tomorrow. So the code is captured the moment they arrive and kept until
 * they either sign up or it goes stale.
 *
 * Stored, not held in memory: a reload between arriving and signing up is the
 * normal case, not the edge case.
 *
 * ── Why the URL is cleaned afterwards ──────────────────────────────────────
 *
 * A visitor who arrives on ?aff_id=… and then copies the address out of their
 * bar is sharing someone else's referral code without knowing it. Stripping the
 * parameter once it is banked stops that, and by then the code is safe in
 * storage.
 */

const KEY = "xfincompass-referral-pending";

/** How long a captured code stays good. Long enough to survive "I'll sign up
 *  later this week", short enough that a code from six months ago does not
 *  quietly attach itself to an unrelated sign-up. */
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Accepted spellings. `aff_id` is what our own links carry; the other two are
 *  what people put in a campaign URL by hand, and refusing them would only mean
 *  losing attributions we could have had. */
const PARAMS = ["aff_id", "ref", "referral_code"];

type Stored = { code: string; at: number };

/** Codes are `re-` + 16 chars from an unambiguous alphabet (see the BE). Reject
 *  anything else rather than posting junk to the API on every sign-up. */
function looksLikeCode(raw: string): boolean {
  return /^[A-Za-z0-9_-]{4,64}$/.test(raw);
}

function bank(code: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ code, at: Date.now() } satisfies Stored));
  } catch {
    // Private mode, storage full: the visitor can still sign up, they just
    // arrive unattributed. Never worth failing a page load over.
  }
}

/**
 * Reads a referral code out of the current URL, banks it, and removes it from
 * the address bar. Safe to call on every boot; does nothing when there is no
 * code to find. Returns what it captured, if anything.
 */
export function captureReferralFromURL(): string | null {
  if (typeof window === "undefined") return null;

  const url = new URL(window.location.href);
  const hit = PARAMS.find((p) => url.searchParams.has(p));
  if (!hit) return null;

  const raw = (url.searchParams.get(hit) ?? "").trim();

  // Strip it either way. A malformed code is still someone else's referral
  // sitting in an address the visitor might paste somewhere.
  PARAMS.forEach((p) => url.searchParams.delete(p));
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);

  if (!looksLikeCode(raw)) return null;
  bank(raw);
  return raw;
}

/** The banked code, or null when there is none or it has gone stale. */
export function pendingReferralCode(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { code, at } = JSON.parse(raw) as Stored;
    if (!code || !looksLikeCode(code)) return null;
    if (Date.now() - at > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

/** Called once the sign-up that used it has gone through. Leaving it behind
 *  would attach the same referrer to the next account created in this browser,
 *  and a shared laptop is enough to make that happen. */
export function clearPendingReferralCode(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}

/** Lets someone enter a code they were given by message or word of mouth. Same
 *  store, so everything downstream still reads one place. */
export function setPendingReferralCode(code: string): void {
  const trimmed = code.trim();
  if (looksLikeCode(trimmed)) bank(trimmed);
}
